import { useEffect, useRef, useState } from "react";
import { Archive, Bookmark, ExternalLink, Loader2, RotateCcw, ThumbsDown } from "lucide-react";

import type { GrantRadarDecision, GrantRadarFinding } from "@/api/grant_radar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export const AVAILABILITY: Record<GrantRadarFinding["availability"], { label: string; className: string }> = {
  open: { label: "Open now", className: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200" },
  rolling: { label: "Rolling", className: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200" },
  forecast: { label: "Upcoming", className: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200" },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground" },
  prior_round: { label: "Past round", className: "bg-muted text-muted-foreground" },
  unknown: { label: "Unconfirmed", className: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" },
};

const FUNDING_TYPE: Record<string, string> = {
  grant: "Grant",
  loan: "Loan, not a grant",
  grant_and_loan: "Grant and loan",
  technical_assistance: "Technical assistance",
  rebate_or_incentive: "Rebate or incentive",
  other: "Other funding",
};

const AMOUNT_BASIS: Record<string, string> = {
  program_total: "program total, not per award",
  prior_award: "a past award, not a current limit",
};

const DECIDED: Record<GrantRadarDecision, string> = { saved: "Saved", does_not_fit: "Marked doesn't fit", archived: "Archived" };

function formatDate(value: string | null) {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function deadlineLabel(finding: GrantRadarFinding) {
  if (finding.deadline_on) return formatDate(finding.deadline_on);
  if (finding.availability === "rolling") return "Rolling";
  return finding.deadline_text || "Not stated";
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

type Props = {
  finding: GrantRadarFinding | null;
  saving: boolean;
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onDecide: (finding: GrantRadarFinding, decision: GrantRadarDecision | null, reason?: string) => void;
};

export function GrantRadarFindingDialog({ finding, saving, error, onOpenChange, onDecide }: Props) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");

  const bodyRef = useRef<HTMLDivElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setReasonOpen(false);
    setReason(finding?.decision_reason ?? "");
  }, [finding?.id, finding?.decision_reason]);

  // Each grant starts at the top; the dialog stays mounted while moving between grants.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the grant changes
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [finding?.id]);

  // The reason field renders below the sources, often out of view; bring it and its buttons in.
  useEffect(() => {
    if (!reasonOpen) return;
    const body = bodyRef.current;
    body?.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
    reasonRef.current?.focus({ preventScroll: true });
  }, [reasonOpen]);

  if (!finding) return null;

  const availability = AVAILABILITY[finding.availability];
  const toConfirm = finding.criteria.filter((criterion) => criterion.status !== "met");
  const matched = finding.criteria.length - toConfirm.length;
  const serves = finding.geography_detail || (finding.geography_scope === "national" ? "Nationwide" : finding.geography_states.join(", "));
  // Short facts share rows; "Serves" is usually a sentence, so it spans the full width.
  const facts: [string, string | null, boolean?][] = [
    ["Type", FUNDING_TYPE[finding.funding_type] ?? finding.funding_type],
    [finding.availability === "forecast" ? "Expected" : "Deadline", deadlineLabel(finding)],
    [
      "Amount",
      finding.award_amount ? `${finding.award_amount}${finding.award_amount_basis && AMOUNT_BASIS[finding.award_amount_basis] ? ` (${AMOUNT_BASIS[finding.award_amount_basis]})` : ""}` : null,
    ],
    ["Match", finding.match_requirement],
    ["Serves", serves || null, true],
  ];
  const decide = (decision: GrantRadarDecision | null, decisionReason?: string) => onDecide(finding, decision, decisionReason);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(85vh,48rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 [scrollbar-gutter:auto]">
        <DialogHeader className="border-b px-6 pb-4 pt-6 pr-12">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={`font-normal ${availability.className}`}>
              {availability.label}
            </Badge>
            {finding.decision ? <Badge variant="outline" className="font-normal">{DECIDED[finding.decision]}</Badge> : null}
          </div>
          <DialogTitle className="pt-1 leading-snug">
            {finding.program_name}
            {finding.round_label ? <span className="font-normal text-muted-foreground"> · {finding.round_label}</span> : null}
          </DialogTitle>
          <DialogDescription>{finding.funder}</DialogDescription>
        </DialogHeader>

        <div ref={bodyRef} className="ordinal-scrollbar min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5 text-sm">
          {finding.description ? <p className="leading-relaxed">{finding.description}</p> : null}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg bg-muted/50 p-4">
            {facts.map(([label, value, wide]) =>
              value ? (
                <div key={label} className={cn("min-w-0 space-y-1", wide && "col-span-2")}>
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
                  <dd className="break-words">{value}</dd>
                </div>
              ) : null,
            )}
          </dl>

          <section className="space-y-2">
            <h3 className="font-medium">Confirm eligibility</h3>
            {toConfirm.length > 0 ? (
              <ul className="space-y-3">
                {toConfirm.map((criterion) => (
                  <li key={criterion.criterion + criterion.requirement} className="flex gap-3">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" aria-hidden />
                    <div className="min-w-0 space-y-0.5">
                      <p>
                        <span className="font-medium">{criterion.criterion}:</span> {criterion.requirement}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {criterion.status === "conflicting" ? "Sources disagree. " : ""}
                        {criterion.profile_fact ? `Your profile says: ${criterion.profile_fact}` : "Not in your profile."}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">Nothing to confirm in the requirements Grant Radar found.</p>
            )}
            {matched > 0 ? (
              <p className="text-xs text-muted-foreground">
                {matched} {matched === 1 ? "requirement matches" : "requirements match"} your profile. Confirm all details with the funder before applying.
              </p>
            ) : null}
          </section>

          {finding.sources.length > 0 ? (
            <section className="space-y-2">
              <h3 className="font-medium">Sources</h3>
              <ul className="space-y-1">
                {finding.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noreferrer" className="inline-flex items-start gap-1.5 text-primary underline-offset-2 hover:underline">
                      <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="min-w-0 break-words">{source.title && source.title !== source.url ? source.title : hostOf(source.url)}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {reasonOpen ? (
            <div className="space-y-2 rounded-md border p-3">
              <Label htmlFor="radar-decision-reason">Why doesn't it fit? (helps future checks)</Label>
              <Textarea ref={reasonRef} id="radar-decision-reason" rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setReasonOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={saving} onClick={() => decide("does_not_fit", reason)}>
                  Mark doesn't fit
                </Button>
              </div>
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        {!reasonOpen ? (
          <DialogFooter className="gap-2 border-t px-6 py-4 sm:justify-between">
            {finding.decision ? (
              <Button variant="ghost" disabled={saving} onClick={() => decide(null)}>
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Move to To review
              </Button>
            ) : (
              <span />
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin self-center text-muted-foreground" aria-label="Saving" /> : null}
              {finding.decision !== "does_not_fit" ? (
                <Button variant="outline" disabled={saving} onClick={() => setReasonOpen(true)}>
                  <ThumbsDown className="mr-1.5 h-4 w-4" />
                  Doesn't fit
                </Button>
              ) : null}
              {finding.decision !== "archived" ? (
                <Button variant="outline" disabled={saving} onClick={() => decide("archived")}>
                  <Archive className="mr-1.5 h-4 w-4" />
                  Archive
                </Button>
              ) : null}
              {finding.decision !== "saved" ? (
                <Button disabled={saving} onClick={() => decide("saved")}>
                  <Bookmark className="mr-1.5 h-4 w-4" />
                  Save
                </Button>
              ) : null}
            </div>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
